<template>
  <div class="membercard-manager">
    <section v-if="!(data.membercard && data.membercard.startDate)" class="membercard-empty">
      <span class="membercard-empty__icon">
        <v-icon size="34">mdi-card-account-details-star-outline</v-icon>
      </span>
      <div>
        <span class="membercard-eyebrow">NOCH NICHT AKTIVIERT</span>
        <h3>{{ elevenDaysActive ? 11 : 10 }}-Tage-Mitgliedskarte starten</h3>
        <p>Startdatum, Studio und betreuende Person festlegen. Der zugehörige Lead wird automatisch vorbereitet.</p>
      </div>
      <v-btn color="primary" depressed class="text-none font-weight-bold" @click="startMembercardDialog">
        <v-icon left>mdi-play-circle-outline</v-icon>
        Mitgliedskarte aktivieren
      </v-btn>
    </section>

    <template v-else>
      <section class="membercard-overview" :class="{ 'membercard-overview--expired': membercardExpired }">
        <header class="membercard-overview__header">
          <div class="membercard-overview__title">
            <span class="membercard-card-icon"><v-icon>mdi-card-account-details-star-outline</v-icon></span>
            <div>
              <span class="membercard-eyebrow">{{ elevenDaysActive ? 11 : 10 }}-TAGE-KARTE</span>
              <h3>Mitgliedskarte im Überblick</h3>
            </div>
          </div>
          <span class="membercard-status" :class="`membercard-status--${membercardStatus.tone}`">
            <v-icon x-small>{{ membercardStatus.icon }}</v-icon>
            {{ membercardStatus.label }}
          </span>
        </header>

        <div class="membercard-metrics">
          <article>
            <span><v-icon small>mdi-calendar-start-outline</v-icon> Start</span>
            <strong>{{ $moment(data.membercard.startDate).format("DD.MM.YYYY") }}</strong>
          </article>
          <article>
            <span><v-icon small>mdi-calendar-end-outline</v-icon> Gültig bis</span>
            <strong>{{ $moment(data.membercard.endDate).format("DD.MM.YYYY") }}</strong>
          </article>
          <article>
            <span><v-icon small>mdi-calendar-clock-outline</v-icon> Restzeit</span>
            <strong>{{ membercardExpired ? "Abgelaufen" : `${membercardDaysLeft} Tage` }}</strong>
          </article>
          <article>
            <span><v-icon small>mdi-door-open</v-icon> Check-ins</span>
            <strong>{{ membercardCheckins.length }}</strong>
          </article>
        </div>

        <div class="membercard-progress">
          <div class="membercard-progress__copy">
            <span>Gültigkeitszeitraum</span>
            <strong>{{ membercardValidityProgress }}%</strong>
          </div>
          <div class="membercard-progress__track">
            <i :style="{ width: `${membercardValidityProgress}%` }"></i>
          </div>
        </div>

        <div class="membercard-controls">
          <div class="membercard-lead" :class="{ 'membercard-lead--missing': !leadFound }">
            <v-icon small>{{ leadFound ? "mdi-link-variant" : "mdi-link-variant-off" }}</v-icon>
            <span>{{ leadFound ? "Lead ist verknüpft" : "Kein Lead gefunden" }}</span>
            <v-btn v-if="!leadFound" x-small text color="primary" class="text-none" @click="createLead"> Lead erstellen </v-btn>
          </div>
          <div class="membercard-controls__actions">
            <v-switch v-model="data.membercard.isActive" dense hide-details color="primary" label="Karte freigegeben" />
            <v-btn color="primary" depressed class="text-none font-weight-bold" @click="updateMembercard">
              <v-icon left small>mdi-content-save-outline</v-icon>
              Speichern
            </v-btn>
          </div>
        </div>
      </section>

      <section class="membercard-checkins">
        <header class="membercard-checkins__header">
          <div>
            <span class="membercard-eyebrow">BESUCHSVERLAUF</span>
            <h3>Check-ins der Mitgliedskarte</h3>
            <p>Manuell ergänzte und bereits erfasste Eintritte.</p>
          </div>
          <v-btn outlined color="primary" class="text-none font-weight-bold" @click="openNewCheckinDialog">
            <v-icon left small>mdi-plus</v-icon>
            Check-in hinzufügen
          </v-btn>
        </header>

        <div v-if="sortedMembercardCheckins.length" class="membercard-checkin-list">
          <article v-for="(checkin, index) in sortedMembercardCheckins" :key="`${checkin.date}-${checkin.originalIndex}`">
            <span class="membercard-checkin__number">{{ sortedMembercardCheckins.length - index }}</span>
            <span class="membercard-checkin__icon"><v-icon small>mdi-door-open</v-icon></span>
            <div>
              <strong>{{ $moment(checkin.date).format("dddd, DD.MM.YYYY") }}</strong>
              <span>{{ $moment(checkin.date).format("HH:mm") }} Uhr</span>
            </div>
            <v-btn icon small color="error" title="Check-in entfernen" aria-label="Check-in entfernen" @click="removeCheckin(checkin.originalIndex)">
              <v-icon small>mdi-delete-outline</v-icon>
            </v-btn>
          </article>
        </div>
        <div v-else class="membercard-checkins__empty">
          <v-icon>mdi-calendar-blank-outline</v-icon>
          <span>Noch keine Check-ins erfasst</span>
        </div>
      </section>
    </template>

    <v-dialog v-model="showNewCheckinDialog" max-width="460px" content-class="membercard-dialog">
      <v-card dark>
        <v-card-title>Check-in hinzufügen</v-card-title>
        <v-card-text>
          <v-date-picker v-if="editNewCheckinDate" v-model="newCheckinDate" dark color="primary" locale="de-CH" full-width />
          <button v-else type="button" class="membercard-dialog__selection" @click="editNewCheckinDate = true">
            <v-icon>mdi-calendar-outline</v-icon>
            <span><small>Datum</small>{{ $moment(newCheckinDate).format("DD.MM.YYYY") }}</span>
            <v-icon small>mdi-pencil-outline</v-icon>
          </button>
          <v-time-picker v-if="editNewCheckinTime" v-model="newCheckinTime" dark color="primary" format="24hr" full-width />
          <button v-else type="button" class="membercard-dialog__selection" @click="editNewCheckinTime = true">
            <v-icon>mdi-clock-outline</v-icon>
            <span><small>Uhrzeit</small>{{ newCheckinTime }} Uhr</span>
            <v-icon small>mdi-pencil-outline</v-icon>
          </button>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text class="text-none" @click="showNewCheckinDialog = false">Abbrechen</v-btn>
          <v-btn
            color="primary"
            depressed
            class="text-none font-weight-bold"
            :loading="checkinSaving"
            :disabled="checkinSaving"
            @click="addNewCheckinWithDateTime">
            Check-in speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showMembercardDialog" max-width="560px" content-class="membercard-dialog">
      <v-card dark>
        <v-card-title class="membercard-dialog__title">
          <span><v-icon>mdi-card-plus-outline</v-icon></span>
          <div>
            <small>NEUE MITGLIEDSKARTE</small>
            <strong>{{ elevenDaysActive ? 11 : 10 }}-Tage-Karte aktivieren</strong>
          </div>
        </v-card-title>
        <v-card-text>
          <v-form>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="membercardStartDate"
                  dark
                  outlined
                  dense
                  hide-details
                  label="Startdatum"
                  prepend-inner-icon="mdi-calendar"
                  type="date" />
              </v-col>
              <v-col cols="12" sm="6">
                <div class="membercard-dialog__enddate">
                  <small>Gültig bis</small>
                  <strong>{{ membercardEndDate }}</strong>
                </div>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="studio"
                  dark
                  outlined
                  dense
                  hide-details
                  label="Studio"
                  prepend-inner-icon="mdi-store-outline"
                  :items="studios.map((entry) => ({ text: entry.name, value: entry.id }))" />
              </v-col>
              <v-col v-if="!employeeInfos._id" cols="12">
                <user-search :value="employeeInfos._id" ignoreRights instructor label="Betreuende Person" @update="updateEmployee" />
              </v-col>
              <v-col v-else cols="12">
                <div class="membercard-dialog__employee">
                  <span><v-icon>mdi-account-check-outline</v-icon></span>
                  <div>
                    <small>Betreuende Person</small>
                    <strong>{{ employeeInfos.firstName }} {{ employeeInfos.lastName }}</strong>
                  </div>
                  <v-btn icon small title="Trainer wechseln" @click="employeeInfos = { _id: null }">
                    <v-icon small>mdi-swap-horizontal</v-icon>
                  </v-btn>
                </div>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="notes"
                  dark
                  outlined
                  dense
                  hide-details
                  rows="3"
                  label="Notizen"
                  placeholder="Optionaler Hinweis zur Mitgliedskarte" />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text class="text-none" @click="showMembercardDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" depressed class="text-none font-weight-bold" @click="setMembercardStartDate(data._id)"> Aktivieren </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "../../../api"
import userService from "../../../services/userService"
import userSearch from "@/components/userSearch.vue"
import { mapGetters } from "vuex"

export default {
  components: { userSearch },
  props: {
    user: {
      required: true,
    },
  },
  data() {
    return {
      userService,
      data: {},
      membercardStartDate: null,
      membercardEndDate: null,
      showMembercardDialog: false,
      showNewCheckinDialog: false,
      newCheckinDate: null,
      editNewCheckinDate: false,
      editNewCheckinTime: false,
      newCheckinTime: null,
      checkinSaving: false,
      leadFound: false,
      studios: [],
      studio: null,
      notes: "",
      employeeInfos: { _id: null },
    }
  },
  async created() {
    this.data = this.user
    this.fetchLead()
    this.$emit("loaded")
    try {
      let studios = await api.fetchStudios()
      const filterCodes = ["ot", "alibey"]
      studios = studios.filter((entry) => !filterCodes.includes(entry.code)).map((entry) => ({ id: entry._id, name: entry.name }))

      this.studios = studios
    } catch (error) {
      this.$toast.error("Fehler beim Laden der Studios")
    }
    try {
      const userStudio = await api.getUserStudio()
      if (userStudio && userStudio.studio && userStudio.studio._id) this.studio = userStudio.studio._id
    } catch (error) {
      this.$toast.error("Fehler beim Laden des Studios")
    }
  },
  methods: {
    async updateEmployee(id) {
      try {
        const employeeManager = await api.getUserForManager(id)
        this.employeeInfos = {
          _id: id,
          firstName: employeeManager.vorname,
          lastName: employeeManager.nachname,
          email: employeeManager.email,
        }
      } catch (error) {
        this.$toast.error("Fehler beim Laden des Accounts")
      }
      this.$forceUpdate()
    },
    async createLead() {
      try {
        await api.createMembercardLead(this.data._id)
        this.leadFound = true
        this.$toast.success("Lead erstellt")
      } catch (error) {
        this.$toast.error("Fehler bei Erstellung")
      }
    },
    async fetchLead() {
      try {
        const status = await api.fetchMembercardLead(this.data._id)
        this.leadFound = Boolean(status && status.found)
      } catch (error) {
        this.leadFound = false
      }
    },
    async updateMembercard() {
      try {
        await this.persistMembercard()
        this.$toast.success("Membercard gespeichert")
      } catch (error) {
        this.$toast.error("Fehler beim Speichern")
      }
    },
    persistMembercard() {
      return api.updateMembercard({
        userId: this.user._id,
        membercard: this.data.membercard,
        actorId: this.actingEmployeeId,
      })
    },
    checkMembercardActiveDate(membercard) {
      if (!membercard || !membercard.endDate || !membercard.isActive) return false
      return !this.$moment().isAfter(this.membercardEndMoment(membercard.endDate))
    },
    membercardEndMoment(value) {
      const endDate = this.$moment(value)
      if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) endDate.endOf("day")
      return endDate
    },
    setMembercardStartDate(id) {
      if (!(this.employeeInfos._id && this.employeeInfos._id.length > 3)) {
        this.$toast.error("Bitte wähle einen Trainer aus")
        return false
      }
      if (!(this.studio && this.studio.length > 3)) {
        this.$toast.error("Bitte wähle ein Studio aus")
        return false
      }

      this.showMembercardDialog = false
      this.startMembercard(id)
    },
    startMembercardDialog() {
      this.membercardStartDate = this.$moment().format("YYYY-MM-DD")
      this.showMembercardDialog = true
    },
    openNewCheckinDialog() {
      this.showNewCheckinDialog = true
      this.newCheckinDate = this.$moment().format("YYYY-MM-DD")
      this.newCheckinTime = this.$moment().format("HH:mm")
      this.editNewCheckinDate = false
      this.editNewCheckinTime = false
    },
    async startMembercard(id) {
      try {
        const memberCardInfos = {
          userId: id,
          startDate: this.$moment(this.membercardStartDate).format("YYYY-MM-DD"),
          studio: this.studio,
          employee: this.employeeInfos._id,
          notes: this.notes,
        }
        const apiData = await api.startMembercard(memberCardInfos)
        if (apiData.membercard) this.$set(this.data, "membercard", apiData.membercard)
        this.$toast.success("Mitgliedskarte erfolgreich erstellt")
      } catch (error) {
        this.$toast.error("Fehler beim Erstellen der Membercard")
      }
    },
    createMembercard(id) {
      api.createMembercard(id).then((data) => {
        if (data.status == 1) {
          alert("erfolgreich erstellt")
        } else {
          alert("es gab einen Fehler")
        }
      })
    },
    editCheckin(index) {
      return this.data.membercard.checkIns[index].date
    },
    removeCheckin(index) {
      if (confirm("Wollen Sie diesen Check-in wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.")) {
        this.data.membercard.checkIns.splice(index, 1)
      }
    },
    async addNewCheckinWithDateTime() {
      if (!this.newCheckinDate || !this.newCheckinTime || this.checkinSaving) return
      if (!Array.isArray(this.data.membercard.checkIns)) this.$set(this.data.membercard, "checkIns", [])
      const newCheckin = {
        date: new Date(`${this.newCheckinDate}T${this.newCheckinTime}`),
      }
      this.data.membercard.checkIns.push(newCheckin)
      this.checkinSaving = true

      try {
        await this.persistMembercard()
        this.showNewCheckinDialog = false
        this.$toast.success("Check-in gespeichert")
      } catch (error) {
        const checkinIndex = this.data.membercard.checkIns.indexOf(newCheckin)
        if (checkinIndex !== -1) this.data.membercard.checkIns.splice(checkinIndex, 1)
        this.$toast.error("Check-in konnte nicht gespeichert werden")
      } finally {
        this.checkinSaving = false
      }
    },
    addNewCheckin() {
      if (!Array.isArray(this.data.membercard.checkIns)) this.$set(this.data.membercard, "checkIns", [])
      this.data.membercard.checkIns.push({ date: new Date() })
    },
  },
  watch: {
    membercardStartDate() {
      let amount = 9
      if (this.elevenDaysActive) amount = 10
      this.membercardEndDate = this.$moment(this.membercardStartDate).add(amount, "days").format("DD.MM.YYYY")
    },
  },
  computed: {
    ...mapGetters("studioStore", ["getSelectedEmployee"]),
    actingEmployeeId() {
      const selectedEmployee = this.getSelectedEmployee || {}
      if (selectedEmployee.user && typeof selectedEmployee.user === "object") return selectedEmployee.user._id || null
      return selectedEmployee.user || selectedEmployee.userId || selectedEmployee._id || null
    },
    membercardCheckins() {
      return this.data && this.data.membercard && Array.isArray(this.data.membercard.checkIns) ? this.data.membercard.checkIns : []
    },
    sortedMembercardCheckins() {
      return this.membercardCheckins
        .map((checkin, originalIndex) => ({ ...checkin, originalIndex }))
        .sort((a, b) => new Date(b.date) - new Date(a.date))
    },
    membercardExpired() {
      const membercard = this.data && this.data.membercard
      if (!membercard || !membercard.endDate) return false
      return this.$moment().isAfter(this.membercardEndMoment(membercard.endDate))
    },
    membercardStatus() {
      const membercard = this.data && this.data.membercard
      if (this.membercardExpired) {
        return { label: "Abgelaufen", tone: "expired", icon: "mdi-card-off-outline" }
      }
      if (!membercard || !membercard.isActive) {
        return { label: "Pausiert", tone: "paused", icon: "mdi-pause-circle-outline" }
      }
      return { label: "Aktiv", tone: "active", icon: "mdi-check-circle-outline" }
    },
    membercardDaysLeft() {
      const membercard = this.data && this.data.membercard
      if (!membercard || !membercard.endDate || this.membercardExpired) return 0
      return Math.max(0, this.membercardEndMoment(membercard.endDate).startOf("day").diff(this.$moment().startOf("day"), "days") + 1)
    },
    membercardValidityProgress() {
      const membercard = this.data && this.data.membercard
      if (!membercard || !membercard.startDate || !membercard.endDate) return 0
      const start = this.$moment(membercard.startDate)
      const end = this.membercardEndMoment(membercard.endDate)
      if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return 0
      const elapsed = this.$moment().diff(start, "minutes")
      const total = end.diff(start, "minutes")
      return Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)))
    },
    elevenDaysActive() {
      return this.$moment().isAfter(this.$moment("01-05-2024", "DD-MM-YYYY"))
    },
    isLocal() {
      return location.host.includes("local")
    },
  },
}
</script>

<style scoped>
.membercard-manager {
  padding: 20px 24px 0;
  color: #f4f7f5;
}

.membercard-empty,
.membercard-overview,
.membercard-checkins {
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 20px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.035), rgba(0, 0, 0, 0.16));
}

.membercard-empty {
  display: grid;
  min-height: 210px;
  align-items: center;
  gap: 20px;
  padding: 30px;
  grid-template-columns: 58px minmax(0, 1fr) auto;
}

.membercard-empty__icon,
.membercard-card-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 150, 64, 0.24);
  color: #71d096;
  background: rgba(0, 150, 64, 0.12);
}

.membercard-empty__icon {
  width: 58px;
  height: 58px;
  border-radius: 18px;
}

.membercard-empty__icon .v-icon,
.membercard-card-icon .v-icon {
  color: inherit !important;
}

.membercard-eyebrow {
  display: block;
  margin-bottom: 5px;
  color: #65ca8b;
  font-size: 12px;
  font-weight: 850;
  letter-spacing: 0.12em;
}

.membercard-empty h3,
.membercard-overview h3,
.membercard-checkins h3 {
  margin: 0;
  color: #ffffff;
  font-size: 21px;
  letter-spacing: -0.025em;
}

.membercard-empty p,
.membercard-checkins p {
  margin: 6px 0 0;
  color: #9fa6a1;
  font-size: 13px;
}

.membercard-overview {
  padding: 24px;
  background: radial-gradient(circle at 95% 0%, rgba(0, 150, 64, 0.13), transparent 35%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.035), rgba(0, 0, 0, 0.16));
}

.membercard-overview--expired {
  background: radial-gradient(circle at 95% 0%, rgba(239, 68, 68, 0.1), transparent 35%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.035), rgba(0, 0, 0, 0.16));
}

.membercard-overview__header,
.membercard-checkins__header,
.membercard-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.membercard-overview__title {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 13px;
}

.membercard-card-icon {
  width: 46px;
  height: 46px;
  flex: 0 0 auto;
  border-radius: 14px;
}

.membercard-status {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
}

.membercard-status .v-icon {
  color: inherit !important;
}

.membercard-status--active {
  border-color: rgba(0, 150, 64, 0.3);
  color: #7bd49d;
  background: rgba(0, 150, 64, 0.1);
}

.membercard-status--expired {
  border-color: rgba(239, 68, 68, 0.3);
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.1);
}

.membercard-status--paused {
  border-color: rgba(148, 163, 184, 0.25);
  color: #c1c8ce;
  background: rgba(148, 163, 184, 0.09);
}

.membercard-metrics {
  display: grid;
  margin-top: 22px;
  gap: 10px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.membercard-metrics article {
  min-width: 0;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 15px;
  background: rgba(0, 0, 0, 0.13);
}

.membercard-metrics article > span {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #979f99;
  font-size: 12px;
}

.membercard-metrics .v-icon {
  color: #72cf95 !important;
}

.membercard-metrics strong {
  display: block;
  margin-top: 8px;
  overflow: hidden;
  color: #f5f7f6;
  font-size: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.membercard-progress {
  margin-top: 18px;
}

.membercard-progress__copy {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #a8aea9;
  font-size: 12px;
}

.membercard-progress__copy strong {
  color: #80d7a1;
}

.membercard-progress__track {
  height: 7px;
  margin-top: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
}

.membercard-progress__track i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #009640, #63d28d);
}

.membercard-controls {
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.membercard-lead {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #7bd49d;
  font-size: 12px;
  font-weight: 700;
}

.membercard-lead .v-icon {
  color: inherit !important;
}

.membercard-lead--missing {
  color: #f3c568;
}

.membercard-controls__actions {
  display: flex;
  align-items: center;
  gap: 14px;
}

.membercard-controls__actions ::v-deep .v-label {
  color: #d7dcd8 !important;
  font-size: 12px !important;
}

.membercard-checkins {
  margin-top: 14px;
  padding: 22px 24px;
}

.membercard-checkin-list {
  display: grid;
  margin-top: 17px;
  gap: 7px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.membercard-checkin-list article {
  display: grid;
  min-height: 62px;
  align-items: center;
  gap: 10px;
  padding: 9px 11px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.025);
  grid-template-columns: 24px 34px minmax(0, 1fr) auto;
}

.membercard-checkin__number {
  color: #6f7771;
  font-size: 12px;
  font-weight: 800;
  text-align: center;
}

.membercard-checkin__icon {
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #71d096;
  background: rgba(0, 150, 64, 0.12);
}

.membercard-checkin__icon .v-icon {
  color: inherit !important;
}

.membercard-checkin-list article > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.membercard-checkin-list article strong {
  overflow: hidden;
  color: #edf1ee;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.membercard-checkin-list article span {
  color: #969e98;
  font-size: 12px;
}

.membercard-checkin-list article .error--text .v-icon {
  color: #ff7077 !important;
}

.membercard-checkins__empty {
  display: flex;
  min-height: 150px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #969e98;
  font-size: 12px;
}

.membercard-checkins__empty .v-icon {
  color: #7d8680 !important;
}

.membercard-dialog__selection {
  display: grid;
  width: 100%;
  min-height: 62px;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 12px;
  color: #eef2ef;
  background: rgba(255, 255, 255, 0.035);
  cursor: pointer;
  grid-template-columns: 30px minmax(0, 1fr) 24px;
  text-align: left;
}

.membercard-dialog__selection .v-icon {
  color: #aeb7b1 !important;
}

.membercard-dialog__selection span {
  display: flex;
  flex-direction: column;
  font-size: 13px;
}

.membercard-dialog__selection small,
.membercard-dialog__title small,
.membercard-dialog__enddate small,
.membercard-dialog__employee small {
  color: #929b95;
  font-size: 12px;
}

.membercard-dialog__title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.membercard-dialog__title > span,
.membercard-dialog__employee > span {
  display: inline-flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  border-radius: 13px;
  color: #71d096;
  background: rgba(0, 150, 64, 0.13);
}

.membercard-dialog__title .v-icon,
.membercard-dialog__employee .v-icon {
  color: inherit !important;
}

.membercard-dialog__title > div {
  display: flex;
  flex-direction: column;
}

.membercard-dialog__title strong {
  color: #ffffff;
  font-size: 18px;
}

.membercard-dialog__enddate,
.membercard-dialog__employee {
  display: flex;
  min-height: 40px;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.13);
}

.membercard-dialog__enddate {
  height: 100%;
  flex-direction: column;
  justify-content: center;
  padding: 6px 12px;
}

.membercard-dialog__enddate strong,
.membercard-dialog__employee strong {
  color: #eff3f0;
  font-size: 12px;
}

.membercard-dialog__employee {
  align-items: center;
  gap: 11px;
  padding: 8px;
}

.membercard-dialog__employee > span {
  width: 36px;
  height: 36px;
  border-radius: 10px;
}

.membercard-dialog__employee > div {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

@media (max-width: 850px) {
  .membercard-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .membercard-checkin-list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .membercard-manager {
    padding: 14px 14px 0;
  }

  .membercard-empty {
    grid-template-columns: 48px minmax(0, 1fr);
  }

  .membercard-empty .v-btn {
    grid-column: 1 / -1;
  }

  .membercard-overview__header,
  .membercard-checkins__header,
  .membercard-controls {
    align-items: flex-start;
    flex-direction: column;
  }

  .membercard-controls__actions {
    width: 100%;
    justify-content: space-between;
  }

  .membercard-checkins__header .v-btn {
    width: 100%;
  }
}
</style>
